import {
  AnswerSource,
  AnswerSourceFilter,
  InsightsChartSpec,
  ProposedAction,
} from 'services/streamInsightsQuery';

import {
  getConversationTitle,
  InsightsChatMessage,
  InsightsConversation,
  InsightsTraceEntry,
} from 'contextProviders/InsightsChatProvider/conversationTypes';

import { stripCtxFooter } from '../stripCtxFooter';

// Serialise an Insights conversation to a Markdown transcript for staff
// download. Includes the staff-only reasoning trace (reasoning blocks + the
// tool / data_call steps) so a reviewer can see HOW the agent reached each
// answer, not just the answer. Fed the LIVE in-memory messages, so the
// reasoning is uncapped (the persisted copy truncates each block).
//
// Pure + deterministic: the caller supplies `exportedAt`, so this is unit
// testable and never reads the clock itself.

const describeFilter = (filter: AnswerSourceFilter): string => {
  const values = filter.values.map(String).join(', ');
  return `${filter.field} ${filter.operator} ${values}`;
};

const sourceLines = (source: AnswerSource, index: number): string[] => {
  const name = source.metricName ?? source.metricId;
  const lines = [`${index}. **${name}** (${source.metricType})`];
  if (source.dataset) {
    lines.push(`   - dataset: ${source.dataset}`);
  }
  const range = `${source.startDate} → ${source.endDate}`;
  lines.push(
    `   - range: ${range}${
      source.dateField ? ` (on ${source.dateField})` : ''
    }`,
  );
  if (source.breakdownField) {
    lines.push(`   - breakdown: ${source.breakdownField}`);
  }
  if (source.trendByCalendarInterval) {
    lines.push(`   - trend: ${source.trendByCalendarInterval}`);
  }
  if (source.filters?.length) {
    lines.push(
      `   - filters: ${source.filters.map(describeFilter).join('; ')}`,
    );
  }
  if (source.fields?.length) {
    const fields = source.fields
      .map((field) =>
        field.sourceColumn
          ? `${field.field} ← ${field.sourceColumn}${
              field.connector ? ` (${field.connector})` : ''
            }`
          : field.field,
      )
      .join('; ');
    lines.push(`   - fields: ${fields}`);
  }
  if (source.compoundExpression) {
    lines.push(`   - formula: ${source.compoundExpression}`);
  }
  if (source.asOf) {
    lines.push(`   - as of: ${source.asOf}`);
  }
  return lines;
};

const traceLines = (trace: InsightsTraceEntry[]): string[] => {
  const lines = ['### Reasoning', ''];
  trace.forEach((entry) => {
    if (entry.kind === 'reasoning') {
      // Blockquote each line so multi-line thinking stays readable in Markdown.
      entry.text.split('\n').forEach((line) => lines.push(`> ${line}`));
    } else if (entry.kind === 'tool') {
      lines.push(`→ ${entry.name}`);
    } else if (entry.kind === 'data_call') {
      lines.push(`  ↳ ${entry.name}${entry.args ? ` — ${entry.args}` : ''}`);
    } else {
      const { kbVersion, always, triggered, nearMissed } = entry.context;
      lines.push(`Context: knowledge base ${kbVersion ?? 'none'}`);
      if (always.length) {
        lines.push(`  Always-on notes: ${always.join(', ')}`);
      }
      if (triggered.length) {
        lines.push(`  Triggered notes: ${triggered.join(', ')}`);
      }
      if (nearMissed.length) {
        lines.push(`  Near-missed notes: ${nearMissed.join(', ')}`);
      }
    }
  });
  lines.push('');
  return lines;
};

const chartLines = (chart: InsightsChartSpec, index: number): string[] => {
  const title = chart.title ?? '(untitled)';
  const lines = [
    `${index}. **${title}** — ${chart.chartType}${
      chart.stacked ? ' · stacked' : ''
    }`,
  ];
  lines.push(
    `   - categories (${chart.categories.length}): ${chart.categories.join(', ')}`,
  );
  chart.series.forEach((series) => {
    lines.push(`   - ${series.name}: [${series.data.join(', ')}]`);
  });
  return lines;
};

const describeProposedAction = (action: ProposedAction): string => {
  if (
    action.kind === 'create_metric' ||
    action.kind === 'create_compound_metric'
  ) {
    return `${action.kind}: "${action.metric.name}"`;
  }
  const title = 'title' in action && action.title ? ` "${action.title}"` : '';
  return `${action.kind}${title} (${action.charts.length} chart(s))`;
};

// The agent closes an answer with the knowledge base's machine-readable
// `<!-- ctx ... -->` footer. It stays in the stored message (the model reads it
// back from history for drill-downs) but a downloaded log is read by a person
// and shared, so strip it here — the same strip the chat render uses.
const withoutCtxFooter = stripCtxFooter;

const messageBlock = (message: InsightsChatMessage, turn: number): string[] => {
  const heading = message.role === 'user' ? 'You' : 'Insights';
  const lines = [`## ${turn} · ${heading}`, ''];

  // The reasoning trace goes ABOVE the answer, mirroring the UI (which shows the
  // disclosure above the prose). Only assistant turns carry a trace.
  if (message.trace?.length) {
    lines.push(...traceLines(message.trace));
  }

  if (message.content.trim()) {
    lines.push(withoutCtxFooter(message.content), '');
  } else if (message.isError) {
    lines.push(
      `_(no answer — error: ${message.errorMessage ?? 'failed'})_`,
      '',
    );
  } else {
    lines.push('_(no content)_', '');
  }

  if (message.charts?.length) {
    lines.push('### Charts', '');
    message.charts.forEach((chart, i) =>
      lines.push(...chartLines(chart, i + 1)),
    );
    lines.push('');
  }

  if (message.sources?.length) {
    lines.push('### Sources', '');
    message.sources.forEach((source, i) =>
      lines.push(...sourceLines(source, i + 1)),
    );
    lines.push('');
  }

  if (message.proposedAction) {
    lines.push('### Proposed action', '');
    lines.push(
      `- ${describeProposedAction(message.proposedAction)} — ${
        message.proposedActionStatus ?? 'pending'
      }`,
      '',
    );
  }

  if (message.suggestions?.length) {
    lines.push('### Suggested follow-ups', '');
    message.suggestions.forEach((suggestion) => lines.push(`- ${suggestion}`));
    lines.push('');
  }

  if (message.feedback) {
    lines.push('### Feedback', '', `- ${message.feedback.verdict}`, '');
  }

  return lines;
};

export const buildInsightsChatLog = ({
  messages,
  conversation,
  exportedAt,
}: {
  messages: InsightsChatMessage[];
  conversation?: InsightsConversation;
  exportedAt: Date;
}): string => {
  const title = getConversationTitle({
    messages,
    customTitle: conversation?.customTitle,
  });
  const meta = [
    `Exported ${exportedAt.toISOString()} · ${messages.length} message(s)`,
    ...(conversation?.createdOn ? [`Created ${conversation.createdOn}`] : []),
  ].join(' · ');

  const lines = [
    '# Insights chat log',
    '',
    `**${title}**`,
    '',
    meta,
    '',
    '---',
    '',
  ];

  messages.forEach((message, index) => {
    lines.push(...messageBlock(message, index + 1));
    lines.push('---', '');
  });

  return lines.join('\n');
};

export default buildInsightsChatLog;
